export type CommunicationSetupIssue = {
  id: string;
  message: string;
  section: string;
  buttonLabel: string;
};

export type CommunicationSetupInput = {
  group: Record<string, unknown>;
  documents: unknown[];
  communicationDocuments: unknown[];
  formData?: Record<string, unknown>;
};

export const COMMUNICATION_SECTIONS = {
  documentTemplates: "document-templates",
  emailTemplates: "email-templates",
  smsTemplates: "sms-templates",
  documentRules: "document-rules",
  emailSettings: "email-settings",
  smsCredentials: "sms-credentials",
  bulkSms: "bulk-sms",
} as const;

export function getCommunicationSetupIssues(
  input: CommunicationSetupInput
): CommunicationSetupIssue[] {
  const { group, documents, communicationDocuments, formData } = input;
  const data = { ...group, ...formData };
  const issues: CommunicationSetupIssue[] = [];

  if (!communicationDocuments?.length) {
    issues.push({
      id: "missing-email-templates",
      message: "No email templates configured.",
      section: COMMUNICATION_SECTIONS.emailTemplates,
      buttonLabel: "Email Templates",
    });
  }

  if (!documents?.length) {
    issues.push({
      id: "missing-document-templates",
      message: "No policy or application document templates configured.",
      section: COMMUNICATION_SECTIONS.documentTemplates,
      buttonLabel: "Document Templates",
    });
  }

  if (data.SmsEnabled) {
    if (!data.SmsApiKey || !data.SmsApiSecret) {
      issues.push({
        id: "sms-credentials",
        message: "SMS is enabled but API credentials are missing.",
        section: COMMUNICATION_SECTIONS.smsCredentials,
        buttonLabel: "SMS Credentials",
      });
    }

    if (!data.ApplicationDocument) {
      issues.push({
        id: "missing-application-doc",
        message: "Application document is not assigned for SMS.",
        section: COMMUNICATION_SECTIONS.documentRules,
        buttonLabel: "Document Rules",
      });
    }

    if (!data.PolicyDocument) {
      issues.push({
        id: "missing-policy-doc",
        message: "Policy document is not assigned for SMS.",
        section: COMMUNICATION_SECTIONS.documentRules,
        buttonLabel: "Document Rules",
      });
    }

    const smsFields = [
      "NewPolicySms",
      "DocumentSms",
      "NonPaymentSms",
      "SmsLapse",
    ] as const;
    const hasEmptySms = smsFields.some(
      (field) => !String(data[field] ?? "").trim()
    );
    if (hasEmptySms) {
      issues.push({
        id: "missing-sms-templates",
        message: "One or more SMS templates are empty.",
        section: COMMUNICATION_SECTIONS.smsTemplates,
        buttonLabel: "SMS Templates",
      });
    }
  }

  if (data.EmailNotification) {
    if (!data.NotificationEmail) {
      issues.push({
        id: "missing-notification-email",
        message:
          "Email notification is enabled but no policy holder notification email is set.",
        section: COMMUNICATION_SECTIONS.emailSettings,
        buttonLabel: "Email Settings",
      });
    }

    const emailTemplateFields = [
      "NewPolicyEmail",
      "DocumentEmail",
      "NonPaymentEmail",
      "LapseEmail",
    ] as const;
    const hasMissingEmailTemplate = emailTemplateFields.some(
      (field) => data[field] == null || data[field] === ""
    );
    if (hasMissingEmailTemplate) {
      issues.push({
        id: "missing-email-event-templates",
        message:
          "Email notification is enabled but one or more event email templates are not assigned.",
        section: COMMUNICATION_SECTIONS.emailSettings,
        buttonLabel: "Email Settings",
      });
    }
  }

  return issues;
}
